import React, { useState, useEffect, useRef, useCallback } from "react";
import Phone from "./Phone";
import imgHomepage from "../assets/images/Homepage.png";
import imgSearching from "../assets/images/Searching For Call.png";

/**
 * Hero carousel.
 * Slide 0 = content slide (copy + TWO phones).
 * Slides 1–3 = full-bleed promo slides (drop a banner <img> in via `image`).
 *
 * To use a real banner image on a promo slide, set its `image` field below.
 */
const PROMO_SLIDES = [
  {
    bg: "bg-grad-1",
    badges: ["🛡️ PCI DSS Compliant", "✔ Verified by Visa"],
    title: "100% Safe & Secure",
    sub: "₹2Cr+ Payments Processed",
    note: "REPLACE WITH BANNER IMAGE",
    image: null, // e.g. "/banners/safe-secure.png"
    alt: "100% Safe & Secure",
  },
  {
    bg: "bg-grad-2",
    badges: ["🛡️ ID + Selfie Verified", "👩 Women-only Mode"],
    title: "Real People. Real Trust.",
    sub: "Every profile verified before you connect",
    note: "REPLACE WITH BANNER IMAGE",
    image: null,
    alt: "Verified people",
  },
  {
    bg: "bg-grad-3",
    badges: ["🎙️ Live Voice Rooms", "📹 Instant Video"],
    title: "Talk the moment it clicks",
    sub: "Join a room, send a gift, make a friend",
    note: "REPLACE WITH BANNER IMAGE",
    image: null,
    alt: "Live rooms",
  },
];

const TOTAL = PROMO_SLIDES.length + 1; // + content slide
const INTERVAL = 5000;

export default function HeroSlider() {
  const [i, setI] = useState(0);
  const timer = useRef(null);
  const phonesRef = useRef(null);

  const go = useCallback((n) => setI(((n % TOTAL) + TOTAL) % TOTAL), []);
  const next = useCallback(() => setI((p) => (p + 1) % TOTAL), []);
  const prev = useCallback(() => setI((p) => (p - 1 + TOTAL) % TOTAL), []);

  const start = useCallback(() => {
    clearInterval(timer.current);
    timer.current = setInterval(() => setI((p) => (p + 1) % TOTAL), INTERVAL);
  }, []);

  useEffect(() => {
    start();
    return () => clearInterval(timer.current);
  }, [start]);

  // mouse parallax on the phone cluster (desktop only)
  useEffect(() => {
    const onMove = (e) => {
      if (window.innerWidth < 900 || !phonesRef.current) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 12;
      const y = (e.clientY / window.innerHeight - 0.5) * 12;
      phonesRef.current.style.transform = `translate(${x}px,${y}px)`;
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const onLight = i === 0;

  return (
    <header className="home-hero">
      <div
        className="slider"
        onMouseEnter={() => clearInterval(timer.current)}
        onMouseLeave={start}
      >
        <div className="slides" style={{ transform: `translateX(-${i * 100}%)` }}>
          {/* SLIDE 0 — content + TWO phones */}
          <div className="slide slide-content">
            <div className="hero-in">
              <div>
                <span className="eyebrow">
                  <span className="dot" />
                  Sleaze-free · Made for Bharat
                </span>
                <h1>
                  Find your <span className="grad">IF</span>.
                </h1>
                <p className="lead">
                  Meet people through what you want to learn, do, or become.
                  Language partners, skill buddies, live rooms — by voice or
                  video, the moment it clicks. India's safest way to make
                  friends. Not a dating app.
                </p>
                <div className="store-row">
                  <button className="btn btn-primary">▶ Get it on Android</button>
                  <button className="btn btn-ghost"> Download for iOS</button>
                </div>
                <div className="hero-note">
                  <div className="av-stack">
                    {["48","26","15","5"].map((n) => (
                      <img key={n} src={`https://i.pravatar.cc/150?img=${n}`} alt="user" style={{ width: 34, height: 34, borderRadius: "50%", border: "2px solid var(--paper)", objectFit: "cover", marginLeft: n === "48" ? 0 : -10, display: "block" }} />
                    ))}
                  </div>
                  Loved by 50,000+ people finding their IF
                </div>
              </div>

              {/* TWO phones only */}
              <div className="hero-phones" ref={phonesRef} aria-hidden="true">
                <div className="cf cf1">
                  <span className="d" style={{ background: "var(--cyan)" }} /> Priya is live 🎙️
                </div>
                <div className="cf cf2">
                  <span className="d" style={{ background: "var(--magenta)" }} /> Voice call →
                </div>
                <Phone variant="left" label="Connect" src={imgSearching} alt="Searching for a call" />
                <Phone variant="center" label="Home" src={imgHomepage} alt="Home screen" />
              </div>
            </div>
          </div>

          {/* PROMO SLIDES */}
          {PROMO_SLIDES.map((s, idx) => (
            <div className="slide" key={idx}>
              <div className={`slide-img ${s.bg}`}>
                {s.image ? (
                  <img src={s.image} alt={s.alt} />
                ) : (
                  <div className="slide-fill">
                    <div className="sf-badges">
                      {s.badges.map((b, k) => (
                        <span className="sf-badge" key={k}>{b}</span>
                      ))}
                    </div>
                    <h2>{s.title}</h2>
                    <p>{s.sub}</p>
                    <div className="sf-note">{s.note}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <button className="slider-arrow prev" aria-label="Previous slide" onClick={() => { prev(); start(); }}>
          ‹
        </button>
        <button className="slider-arrow next" aria-label="Next slide" onClick={() => { next(); start(); }}>
          ›
        </button>

        <div className={`slider-dots ${onLight ? "on-light" : ""}`}>
          {Array.from({ length: TOTAL }).map((_, idx) => (
            <button
              key={idx}
              className={`dot-btn ${idx === i ? "active" : ""}`}
              aria-label={`Go to slide ${idx + 1}`}
              onClick={() => { go(idx); start(); }}
            />
          ))}
        </div>
      </div>
    </header>
  );
}
